import { yupResolver } from '@hookform/resolvers/yup';
import {
  Avatar,
  Box,
  Button,
  Checkbox,
  Dialog,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  FormGroup,
  Grid,
  IconButton,
  InputLabel,
  Stack,
  Typography,
} from '@mui/material';
import { CheckCircle, CheckCircleEmpty, CloseCircle } from 'asset';
import { LoadingPopup } from 'components/common';
import {
  DatePickerField,
  Inputs,
  TimerPickerField,
} from 'components/form-control';
import Select, { GetOptionsResponse } from 'components/form-control/Select';
import PopupEditAddress from 'components/Popups/PopupEditAddress';
import yup from 'config/yup.custom';
import {
  convertDobToAge,
  convertTimeToUTC,
  mergeDateTime,
  NotifyService,
} from 'helpers';
import { useDateTime } from 'hooks';
import {
  ADDRESS_DETAIL_PARENT_DETAIL_TYPE,
  ADDRESS_PARENT_DETAIL_TYPE,
  CHILDREN_PARENT_DETAIL_TYPE,
  GET_PARENTS_PARAMS_REQUEST,
  OptionItem,
  PARENT_TYPE,
} from 'models';
import { BODY_EDIT_BASE_RAC } from 'models/subscription';
import { optionStatus } from 'modules/Concierge/Subscription/data';
import { useEffect, useMemo, useState } from 'react';
import { Controller, useFieldArray, useForm } from 'react-hook-form';
import {
  getParentAddressApi,
  getParentChildrenApi,
  getParentListApi,
} from 'services';
import { updateBaseRequestDetailApi } from 'services/subscription';
import { useAppDispatch, useAppSelector } from 'store/hook';
import { setLoadingPopup } from 'store/reducers/global';
import { getBaseRequestDetailAction } from 'store/reducers/subscription/actionType';
import { theme } from 'theme';
import { DAY_OF_WEEK } from './data';

interface Props {
  open: boolean;
  handleClosePopup?: any;
  onSuccess?: any;
  subject?: any;
  parentId?: any;
}

interface IFormInputs {
  id: string;
  parent?: {
    id: string;
    label: string;
  };
  children: {
    id: string;
    picture: string | null;
    age_range: number;
    full_name: string;
    is_checked: boolean;
    date_of_birth: string;
  }[];
  days_of_week?: any[];
  from_date: string;
  from_time: string;
  to_date: string;
  to_time: string;
  address?: OptionItem | null;
  special_requirements: string;
  has_dog: boolean;
  status?: OptionItem;
  has_cat: boolean;
}

const defaultValues: IFormInputs = {
  id: '',
  parent: undefined,
  children: [],
  days_of_week: [],
  address: undefined,
  special_requirements: '',
  from_date: '',
  from_time: '',
  to_date: '',
  to_time: '',
  status: undefined,
  has_dog: false,
  has_cat: false,
};

const PopupEditBaseRAC = (props: Props) => {
  const { open, handleClosePopup, subject, onSuccess, parentId } = props;

  const { moment } = useDateTime();
  const [openEditAddress, setOpenEditAddress] = useState(false);
  const [openAddAddress, setOpenAddAddress] = useState(false);
  const [days] = useState(DAY_OF_WEEK);
  const { base_request_detail } = useAppSelector(
    (state) => state.subscriptionReducer.subscription
  );
  const { loadingPopup } = useAppSelector((state) => state.globalReducer);
  const dispatch = useAppDispatch();

  const schema = useMemo(() => {
    return yup.object({
      parent: yup.mixed().required('Parent is required'),
      from_time: yup.string().required('From time is required'),
      from_date: yup
        .string()
        .test(
          'from_date',
          'From date must be greater than or equal to today',
          (value) => {
            const today = moment().startOf('day');
            const date = moment(value);
            return date.isSameOrAfter(today);
          }
        )
        .required('From date is required'),
      to_time: yup.string().required('To time is required'),
      to_date: yup
        .string()
        .test(
          'to_date',
          'To date must be greater than from date',
          (value, context) => {
            const fromDate = moment(context.parent.from_date);
            const toDate = moment(value);
            return toDate.isSameOrAfter(fromDate);
          }
        )
        .test(
          'to_date',
          'To date must be greater than or equal to today',
          (value) => {
            const today = moment().startOf('day');
            const date = moment(value);
            return date.isSameOrAfter(today);
          }
        )
        .required('To date is required'),
      children: yup.mixed().notRequired(),
      status: yup.mixed().notRequired(),
      days_of_week: yup.mixed().notRequired(),
      address: yup.mixed().required('Address is required'),
    });
  }, [moment]);

  const form = useForm<IFormInputs>({
    resolver: yupResolver(schema),
    mode: 'onChange',
    defaultValues: defaultValues,
  });
  const { reset, handleSubmit, control, watch, setValue } = form;

  const { fields: days_of_week } = useFieldArray({
    control,
    name: 'days_of_week',
  });

  const {
    fields: children,
    remove: removeChildren,
    append: appendChildren,
  } = useFieldArray({
    control,
    name: 'children',
  });

  const _handleClosePopup = () => {
    reset({ ...defaultValues });
    handleClosePopup();
  };

  useEffect(() => {
    if (parentId) {
      dispatch(
        getBaseRequestDetailAction({ data: { id: parentId }, isPopup: true })
      );
    }
  }, [parentId, dispatch]);

  const getAddressLabel = (data: {
    block_number: string;
    street: string;
    unit_number: string;
    postal_code: string;
  }) => {
    const { block_number, postal_code, street, unit_number } = data;
    return `${unit_number}, ${block_number} ${street}, Singapore ${postal_code}`;
  };

  const onSuccessAddOrEditAddress = (
    data?: ADDRESS_DETAIL_PARENT_DETAIL_TYPE
  ) => {
    const address = form.getValues('address');
    if (address && address.id === data?.id) {
      form.setValue('address', { ...data, label: getAddressLabel(data) });
    }
  };

  useEffect(() => {
    if (base_request_detail) {
      const setFormData = async () => {
        const startDays = moment(
          base_request_detail.start_datetime
        ).toISOString();
        const endDays = moment(base_request_detail.end_datetime).toISOString();

        reset({
          id: base_request_detail.id,
          from_time: startDays,
          from_date: startDays,
          to_time: endDays,
          to_date: endDays,
          address: base_request_detail.address
            ? {
                ...base_request_detail.address,
                label: getAddressLabel(base_request_detail.address),
              }
            : undefined,
          parent: base_request_detail.parent
            ? {
                id: base_request_detail.parent.User.id,
                label: `${base_request_detail.parent.User.first_name} ${base_request_detail.parent.User.last_name}`,
              }
            : undefined,
          children: [],
          days_of_week: base_request_detail.days_of_week
            ? days.map((item: { value: number }) => ({
                ...item,
                isChecked: !!base_request_detail.days_of_week.find(
                  (day) => day === item.value
                ),
              }))
            : days.map((item: any) => ({
                ...item,
                isChecked: false,
              })),
          special_requirements: base_request_detail.special_requirements,
          status:
            optionStatus.find(
              (item) => item.id === base_request_detail.status
            ) || undefined,
          has_dog: base_request_detail?.pets.dogs,
          has_cat: base_request_detail?.pets.cats,
        });
      };
      setFormData();
    }
    //eslint-disable-next-line react-hooks/exhaustive-deps
  }, [base_request_detail]);

  const editRequest = async (data: BODY_EDIT_BASE_RAC) => {
    setLoadingPopup(true);
    await updateBaseRequestDetailApi(subject, data)
      .then((r) => {
        _handleClosePopup();
        onSuccess();
        NotifyService.success(r);
      })
      .catch((e) => NotifyService.error(e))
      .finally(() => {
        setLoadingPopup(false);
      });
  };

  const onSubmit = async (data: IFormInputs) => {
    if (data) {
      const dataUpdate: BODY_EDIT_BASE_RAC = {
        address_id: data.address?.id,
        days_of_week: data.days_of_week
          ?.filter((it) => it.isChecked)
          ?.map((item) => item.value),
        start_datetime:
          convertTimeToUTC(mergeDateTime(data.from_date, data.from_time)) || '',
        end_datetime:
          convertTimeToUTC(mergeDateTime(data.to_date, data.to_time)) || '',
        children_ids: data.children
          ?.filter((it) => it.is_checked)
          ?.map((item) => item.id),
        special_requirements: data.special_requirements,
        ...(optionStatus?.some(
          (e) => e.id === data.status?.id && !e.disabled
        ) && {
          status: data.status?.id,
        }),
        pets: {
          dogs: data.has_dog,
          cats: data.has_cat,
        },
      };
      editRequest(dataUpdate);
    }
  };

  const parent = watch('parent');

  const getOptions =
    (key: keyof IFormInputs) =>
    async (page: number = 1, limit: number = 20, keyword?: string) => {
      let data: GetOptionsResponse | null = null;

      switch (key) {
        case 'parent':
          const paramsParent: GET_PARENTS_PARAMS_REQUEST = {
            page: page,
            limit: limit,
          };
          if (keyword) paramsParent.keyword = keyword;
          data = await getParentListApi(paramsParent)
            .then(({ data }) => {
              return {
                metadata: data.data.metadata,
                options: data.data.records.map((it: PARENT_TYPE) => ({
                  id: it.parent_id,
                  label: `${it.first_name} ${it.last_name}`,
                })),
              };
            })
            .catch((e) => {
              NotifyService.error(e);
              return null;
            });
          break;
        case 'address':
          if (parent?.id) {
            data = await getParentAddressApi(parent.id)
              .then(({ data }) => {
                return {
                  metadata: data.metadata,
                  options: data.records
                    .map((it: ADDRESS_PARENT_DETAIL_TYPE) => ({
                      id: it.id,
                      name: it.address_name,
                      street: it.street_name,
                      block_number: it.block_no,
                      unit_number: it.unit_no,
                      country: it.country,
                      postal_code: it.postal_code,
                    }))
                    .map((it: ADDRESS_DETAIL_PARENT_DETAIL_TYPE) => ({
                      ...it,
                      label: getAddressLabel(it),
                    })),
                };
              })
              .catch((e) => {
                NotifyService.error(e);
                return null;
              });
          }
          break;
      }

      return data;
    };

  useEffect(() => {
    if (parent) {
      const getChildren = async () => {
        const dataChildren: CHILDREN_PARENT_DETAIL_TYPE[] =
          await getParentChildrenApi(parent.id)
            .then(({ data }) => data.records)
            .catch((e) => {
              NotifyService.error(e);
              return [];
            });

        if (dataChildren.length) {
          removeChildren();
          appendChildren(
            dataChildren.map((item) => ({
              id: item.id,
              picture: item.picture,
              age_range: item.age_range,
              full_name: item.children_name,
              date_of_birth: item.date_of_birth,
              is_checked: !!base_request_detail?.children.find(
                (temp) => temp.id === item.id
              ),
            }))
          );
        }
      };
      getChildren();
    } else {
      removeChildren();
    }

    //eslint-disable-next-line react-hooks/exhaustive-deps
  }, [parent]);

  return (
    <Dialog
      open={open}
      sx={{
        '& .MuiPaper-root': {
          width: 523,
          borderRadius: 0,
          height: 'auto',
        },
      }}
    >
      <DialogTitle
        sx={{
          padding: '20px 25px',
        }}
      >
        <Grid
          sx={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
          }}
        >
          <Typography
            sx={{
              fontWeight: 500,
              fontSize: '16px',
              lineHeight: '100%',
            }}
          >
            Edit Base Request
          </Typography>
          <IconButton
            onClick={handleClosePopup}
            sx={{
              padding: 0,
            }}
          >
            <CloseCircle />
          </IconButton>
        </Grid>
      </DialogTitle>
      {loadingPopup && <LoadingPopup color="primary" />}
      <DialogContent
        sx={{
          p: '30px 24px 50px 26px',
        }}
        dividers
      >
        <form onSubmit={handleSubmit(onSubmit)} style={{ height: '100%' }}>
          <Stack height="100%" justifyContent="space-between">
            <Stack spacing="16px">
              <Grid>
                <Select
                  isDisabled={!!subject}
                  title="Parent"
                  name="parent"
                  form={form}
                  isHasMore
                  filterFunc={false}
                  getOptions={getOptions('parent')}
                  onChange={() => {
                    setValue('address', undefined);
                  }}
                />
              </Grid>
              <Grid>
                <Stack direction="row" spacing="14px" alignItems="flex-end">
                  <Stack
                    sx={{
                      width: '50%',
                    }}
                  >
                    <DatePickerField
                      title="Date"
                      form={form}
                      name="from_date"
                    />
                  </Stack>
                  <Typography height="30px" color="#545454">
                    -
                  </Typography>
                  <Stack
                    sx={{
                      width: '50%',
                    }}
                  >
                    <DatePickerField form={form} name="to_date" />
                  </Stack>
                </Stack>
              </Grid>
              <Grid>
                <Stack direction="row" spacing="14px" alignItems="flex-end">
                  <Stack
                    sx={{
                      width: '50%',
                    }}
                  >
                    <TimerPickerField
                      title="Time"
                      form={form}
                      name="from_time"
                    />
                  </Stack>
                  <Typography height="30px" color="#545454">
                    -
                  </Typography>
                  <Stack
                    sx={{
                      width: '50%',
                    }}
                  >
                    <TimerPickerField form={form} name="to_time" />
                  </Stack>
                </Stack>
              </Grid>
              <Grid>
                <Select
                  title="Address"
                  name="address"
                  form={form}
                  isHasMore
                  filterFunc
                  refreshWhenOpen
                  getOptions={getOptions('address')}
                />
              </Grid>

              {base_request_detail && (
                <Box display="flex" sx={{ mt: '9px !important' }}>
                  <Button
                    sx={{
                      padding: '0px',
                      color: '#226fe3',
                      fontSize: '14px',
                      fontWeight: 400,
                      marginRight: '15px',
                    }}
                    onClick={() => setOpenEditAddress(true)}
                  >
                    Edit Address
                  </Button>
                  <Button
                    sx={{
                      padding: '0px',
                      color: '#226fe3',
                      fontSize: '14px',
                      fontWeight: 400,
                    }}
                    onClick={() => setOpenAddAddress(true)}
                  >
                    Add New Address
                  </Button>
                </Box>
              )}
              <Stack spacing="16px">
                <Grid>
                  <Inputs
                    title="Special Requirements"
                    form={form}
                    name="special_requirements"
                  />
                </Grid>
              </Stack>
              <Stack spacing="16px">
                <Grid>
                  <Select
                    name="status"
                    title="Status"
                    options={optionStatus}
                    bindLabel="label"
                    filterFunc={true}
                    form={form}
                  />
                </Grid>
              </Stack>
              <Grid>
                <InputLabel
                  sx={{
                    fontWeight: 500,
                    fontSize: '15px',
                    lineHeight: '100%',
                    color: '#000',
                    marginBottom: '10px',
                  }}
                >
                  Days of Week
                </InputLabel>
                <FormGroup aria-label="position">
                  <Stack direction="row" justifyContent="space-between">
                    {days_of_week.map((item, index) => (
                      <Controller
                        key={index}
                        name={`days_of_week.${index}.isChecked`}
                        control={form.control}
                        render={({ field }) => (
                          <FormControlLabel
                            {...field}
                            sx={{
                              fontSize: '14px',
                              lineHeight: '16px',
                              letterSpacing: '0.05px',
                              color: 'rgba(60, 60, 67, 0.6)',
                              '& .MuiTypography-root': {
                                mt: '5px',
                              },
                            }}
                            value={item.value}
                            labelPlacement="bottom"
                            control={
                              <Checkbox
                                icon={<CheckCircleEmpty />}
                                checkedIcon={<CheckCircle />}
                                checked={field.value ?? false}
                                sx={{
                                  p: 0,
                                }}
                              />
                            }
                            label={item.name}
                          />
                        )}
                      />
                    ))}
                  </Stack>
                </FormGroup>
              </Grid>
              <Grid>
                <InputLabel
                  sx={{
                    fontWeight: 500,
                    fontSize: '15px',
                    lineHeight: '100%',
                    color: '#000',
                    marginBottom: '10px',
                  }}
                >
                  Child
                </InputLabel>
                <FormGroup>
                  <Stack spacing="17px">
                    {children?.map((item, index) => {
                      const child = form.watch(`children.${index}`);
                      return (
                        <Stack
                          key={item.id}
                          direction="row"
                          alignItems="center"
                        >
                          <Controller
                            name={`children.${index}.is_checked`}
                            control={form.control}
                            render={({ field }) => (
                              <Checkbox
                                {...field}
                                checked={field.value ?? false}
                                sx={{
                                  '&.Mui-checked': {
                                    color: theme.palette.secondary.main,
                                  },
                                }}
                              />
                            )}
                          />
                          <Avatar
                            alt={child.full_name}
                            src={child.picture ?? ''}
                          />
                          <Stack ml="8px">
                            <Typography
                              sx={{
                                fontWeight: 600,
                                fontSize: '13px',
                                lineHeight: '20px',
                                letterSpacing: '0.1px',
                              }}
                            >
                              {child.full_name}
                            </Typography>
                            <Typography
                              sx={{
                                fontSize: '13px',
                                lineHeight: '20px',
                                letterSpacing: '0.1px',
                              }}
                            >
                              {convertDobToAge(child.date_of_birth)}
                            </Typography>
                          </Stack>
                        </Stack>
                      );
                    })}
                  </Stack>
                </FormGroup>
              </Grid>
              <Grid>
                <InputLabel
                  sx={{
                    fontWeight: 500,
                    fontSize: '15px',
                    lineHeight: '100%',
                    color: '#000',
                    marginBottom: '10px',
                  }}
                >
                  Pets
                </InputLabel>
                <FormGroup>
                  <Stack>
                    <Stack direction="row" alignItems="center">
                      <Controller
                        name={'has_dog'}
                        control={form.control}
                        render={({ field }) => (
                          <Checkbox
                            {...field}
                            checked={field.value ?? false}
                            sx={{
                              '&.Mui-checked': {
                                color: theme.palette.secondary.main,
                              },
                            }}
                          />
                        )}
                      />
                      <Stack ml="8px">
                        <Typography
                          sx={{
                            fontWeight: 600,
                            fontSize: '13px',
                            lineHeight: '20px',
                            letterSpacing: '0.1px',
                          }}
                        >
                          Dogs
                        </Typography>
                      </Stack>
                    </Stack>
                    <Stack direction="row" alignItems="center">
                      <Controller
                        name={'has_cat'}
                        control={form.control}
                        render={({ field }) => (
                          <Checkbox
                            {...field}
                            checked={field.value ?? false}
                            sx={{
                              '&.Mui-checked': {
                                color: theme.palette.secondary.main,
                              },
                            }}
                          />
                        )}
                      />
                      <Stack ml="8px">
                        <Typography
                          sx={{
                            fontWeight: 600,
                            fontSize: '13px',
                            lineHeight: '20px',
                            letterSpacing: '0.1px',
                          }}
                        >
                          Cats
                        </Typography>
                      </Stack>
                    </Stack>
                  </Stack>
                </FormGroup>
              </Grid>
            </Stack>

            <Stack
              spacing="8px"
              direction="row"
              justifyContent="space-between"
              mt="32px"
            >
              <Button
                variant="outlined"
                color="secondary"
                sx={{
                  width: '233.5px',
                  height: '30px',
                }}
                onClick={() => {
                  reset();
                  _handleClosePopup();
                }}
              >
                Cancel
              </Button>
              <Button
                color="secondary"
                variant="contained"
                sx={{
                  width: '233.5px',
                  height: '30px',
                }}
                type="submit"
              >
                {subject ? 'Update' : 'Create'}
              </Button>
            </Stack>
          </Stack>
        </form>
      </DialogContent>

      {base_request_detail && (openEditAddress || openAddAddress) && (
        <PopupEditAddress
          open
          isEdit={openEditAddress}
          subject={base_request_detail?.parent_id}
          idAddress={form.watch('address')?.id}
          handleClosePopup={() => {
            setOpenAddAddress(false);
            setOpenEditAddress(false);
          }}
          onSuccess={onSuccessAddOrEditAddress}
        />
      )}
    </Dialog>
  );
};

export default PopupEditBaseRAC;
